import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { settingsCache } from '@/utils/settingsCache';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HelpCircle, MessageSquare, Send, ExternalLink, Bot, User, Loader2, Ticket, CheckCircle2 } from 'lucide-react';

interface HelpItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const CATEGORY_ORDER = ['general', 'campaigns', 'billing', 'technical', 'compliance'];

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  campaigns: 'Campaigns',
  billing: 'Billing',
  technical: 'Technical',
  compliance: 'Compliance',
};

const CATEGORY_STYLES: Record<string, string> = {
  general:    'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  campaigns:  'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-800',
  billing:    'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
  technical:  'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  compliance: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800',
};

export default function Help() {
  const { session } = useAuth();
  const [helpItems, setHelpItems] = useState<HelpItem[]>([]);
  const [faqLoading, setFaqLoading] = useState(true);

  // AI chat state
  const [chatEnabled, setChatEnabled] = useState(true);
  const [ticketEnabled, setTicketEnabled] = useState(true);
  const [supportUrl, setSupportUrl] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: "Hi! I'm the Call Sparker support assistant. Ask me anything about setting up campaigns, managing prospects, configuring your scripts, or any other feature." },
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [chatError, setChatError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Ticket submission state
  const [ticketCategory, setTicketCategory] = useState('general');
  const [ticketIssue, setTicketIssue] = useState('');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');

  useEffect(() => {
    fetchFaqItems();
    fetchSettings();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchFaqItems = async () => {
    const { data } = await supabase
      .from('help_items')
      .select('id, question, answer, category')
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    setHelpItems(data ?? []);
    setFaqLoading(false);
  };

  const fetchSettings = async () => {
    const [enabled, ticketOn, url] = await Promise.all([
      settingsCache.getSetting<boolean>('help_ai_chat_enabled', true),
      settingsCache.getSetting<boolean>('ticket_submission_enabled', true),
      settingsCache.getSetting<string>('support_url', ''),
    ]);
    setChatEnabled(enabled);
    setTicketEnabled(ticketOn);
    setSupportUrl(url);
  };

  const groupedItems = CATEGORY_ORDER.reduce<Record<string, HelpItem[]>>((acc, cat) => {
    const items = helpItems.filter(i => i.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.user || !ticketIssue.trim()) return;
    setTicketSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('help_requests')
        .insert({
          user_id: session.user.id,
          email: session.user.email ?? '',
          issue: ticketIssue.trim(),
          category: ticketCategory,
          status: 'open',
        })
        .select('ticket_number')
        .single();

      if (error) throw error;

      const tkt = data?.ticket_number ?? '';
      setTicketNumber(tkt);
      setTicketSubmitted(true);

      // Send confirmation email (fire-and-forget, non-blocking)
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      fetch('https://giedkdxtrqcqzcockacd.supabase.co/functions/v1/send-support-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession?.access_token}`,
        },
        body: JSON.stringify({
          type: 'confirmation',
          to: session.user.email,
          ticketNumber: tkt,
          issue: ticketIssue.trim(),
          category: ticketCategory,
        }),
      }).catch(() => { /* email is best-effort */ });
    } catch {
      // Keep form state so the user can try again
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending) return;

    const userMessage: ChatMessage = { role: 'user', content: text };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setIsSending(true);
    setChatError('');

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const token = currentSession?.access_token;

      const response = await fetch(
        'https://giedkdxtrqcqzcockacd.supabase.co/functions/v1/help-chat',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            messages: nextMessages,
            userId: session?.user.id,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setChatError(data.error ?? 'Something went wrong. Please try again.');
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      }
    } catch {
      setChatError('Could not reach the assistant. Check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <HelpCircle className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold">Help Center</h1>
          <p className="text-muted-foreground">Browse common questions or chat with our AI assistant</p>
        </div>
      </div>

      {/* FAQ Section */}
      <section>
        <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
        {faqLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading...
          </div>
        ) : Object.keys(groupedItems).length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No FAQ items have been added yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedItems).map(([category, items]) => (
              <div key={category}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge
                    variant="outline"
                    className={`text-xs font-semibold px-2.5 py-0.5 ${CATEGORY_STYLES[category] ?? ''}`}
                  >
                    {CATEGORY_LABELS[category] ?? category}
                  </Badge>
                </div>
                <Accordion type="multiple" className="border rounded-lg divide-y">
                  {items.map(item => (
                    <AccordionItem key={item.id} value={item.id} className="px-4">
                      <AccordionTrigger className="text-left text-sm font-medium py-4 hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground pb-4 whitespace-pre-line">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* AI Chat Section */}
      {chatEnabled && (
        <section>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Ask the AI Assistant
          </h2>
          <Card className="flex flex-col">
            <CardHeader className="pb-2 border-b">
              <CardTitle className="text-base flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary" />
                Call Sparker Support Chat
              </CardTitle>
              <CardDescription>
                Powered by AI — trained on Call Sparker documentation and video transcripts
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0 flex flex-col">
              {/* Message thread */}
              <div className="h-80 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex-shrink-0 h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 text-sm whitespace-pre-line ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="flex-shrink-0 h-7 w-7 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                ))}
                {isSending && (
                  <div className="flex gap-3 justify-start">
                    <div className="flex-shrink-0 h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                    <div className="bg-muted rounded-lg px-4 py-2 text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Thinking...
                    </div>
                  </div>
                )}
                {chatError && (
                  <p className="text-sm text-destructive text-center">{chatError}</p>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="flex gap-2 p-4 border-t">
                <Input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a question..."
                  disabled={isSending}
                  className="flex-1"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || isSending}
                  size="icon"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Submit a Ticket Section */}
      {ticketEnabled && <section>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Ticket className="h-5 w-5" />
          Submit a Support Ticket
        </h2>
        {ticketSubmitted ? (
          <Card>
            <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
              <h3 className="font-semibold text-lg">Ticket submitted!</h3>
              <p className="text-muted-foreground text-sm max-w-sm">
                Your ticket <strong>{ticketNumber}</strong> has been received. We've sent a confirmation to <strong>{session?.user.email}</strong>. Our team will follow up via email.
              </p>
              <Button
                variant="outline"
                className="mt-2"
                onClick={() => {
                  setTicketSubmitted(false);
                  setTicketIssue('');
                  setTicketCategory('general');
                  setTicketNumber('');
                }}
              >
                Submit another ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>
                Can't find what you need? Send us a message and we'll follow up by email.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleTicketSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Your email</label>
                  <Input value={session?.user.email ?? ''} disabled className="bg-muted" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Category</label>
                  <Select value={ticketCategory} onValueChange={setTicketCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="campaigns">Campaigns</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Describe your issue</label>
                  <Textarea
                    value={ticketIssue}
                    onChange={e => setTicketIssue(e.target.value)}
                    placeholder="Tell us what's happening and any steps you've already tried..."
                    rows={5}
                    required
                  />
                </div>
                <Button type="submit" disabled={ticketSubmitting || !ticketIssue.trim()}>
                  {ticketSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </section>}

      {/* External support link (optional) */}
      {supportUrl && (
        <section>
          <Card className="border-dashed">
            <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6">
              <div>
                <h3 className="font-semibold">Still need help?</h3>
                <p className="text-sm text-muted-foreground">
                  Submit a ticket through our support portal.
                </p>
              </div>
              <Button asChild variant="outline">
                <a href={supportUrl} target="_blank" rel="noopener noreferrer">
                  Open Support Portal
                  <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
